import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery, useMutation } from '@tanstack/react-query'

import { base64ToBlob } from '@b-stock/fe-core/bstock-react'

import accountApi from '@api/account'
import locationApi from '@api/location'
import personApi from '@api/person'

type GetResellerCertArg = {
  title?: string
  locationId?: string
  accountId?: string
  personId?: string
  signatureImg?: string
  resellerTax?: {
    countryCode: string
    provinceCode: string
  }
}
const getResellerCert = async ({
  resellerTax,
  locationId,
  accountId,
  personId,
  title,
  signatureImg,
}: GetResellerCertArg) => {
  if (!(locationId && accountId && personId)) {
    return null
  }

  const [location, account, person] = await Promise.all([
    locationApi.getAddress({ id: locationId }),
    accountApi.fetchAccount({ id: accountId }),
    accountApi.fetchPerson({ id: personId }),
  ])

  const sigBlob = signatureImg
    ? base64ToBlob(signatureImg.replace(/^(data:.*?,)/, ''), 'image/png')
    : null

  const query = { locationIds: [locationId], title, accountId }

  const data = await locationApi.generateResaleCertificate(sigBlob, query)

  if (resellerTax) {
    location.countryCode = resellerTax.countryCode
    location.provinceCode = resellerTax.provinceCode
  }

  return {
    pdf: data,
    location,
    account,
    person,
  }
}

const submitResellerCert = async (
  fields: GetResellerCertArg & { personId: string }
) => {
  // Generate RC
  const result = await getResellerCert(fields)
  if (!result) {
    throw new Error(
      'Undefined response `getResellerCert`. A field was likely forgotten.'
    )
  }

  // Patch person record with new job title
  await personApi.updatePerson({
    id: fields.personId,
    data: {
      jobTitle: fields.title,
    },
  })
}

const useSubmitResellerCert = () =>
  useMutation({
    mutationFn: submitResellerCert,
  })
const useGeneratedResellerCert = (
  args: GetResellerCertArg,
  opts: Omit<
    UseQueryOptions<
      unknown,
      unknown,
      Awaited<ReturnType<typeof getResellerCert>>,
      any[]
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: ['genResellerCert', args],
    queryFn: () => getResellerCert(args),
    ...opts,
  })

export { useGeneratedResellerCert, useSubmitResellerCert }
